import React, { useState } from 'react';
import { BsX } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Button, Flex, HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import FakeUploadProgressBar from '../../../../../components/CertificateCreationFlow/components/FakeUploadProgressBar';
import FileDropzone from '../../../../../components/CertificateCreationFlow/components/FileDropZone';
import { createErrorToast } from '../../../../../redux/modules/app';
import { hideModal, showModal } from '../../../../../redux/modules/modal';
import {
  deleteContract,
  uploadContract
} from '../../../../../services/contract-service';
import AiExtractContractRequirementsModal from '../AiExtractContractRequirementsModal';

const TemplatesFromContractModal = ({ companyId, onExtractionComplete = null }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [preparedContracts, setPreparedContracts] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const handleFileDrop = async files => {
    setIsUploading(true);

    try {
      const contractPromises = files.map(file => uploadContract(companyId, file));

      const contracts = await Promise.all(contractPromises);

      // append new uploaded contracts to any already prepared so multiple drops don't orphan previous uploads
      setPreparedContracts(prev => [...prev, ...contracts]);
      setIsUploading(false);
    } catch (e) {
      setIsUploading(false);
      dispatch(createErrorToast({
        title: 'Failed to upload contract',
        error: e
      }));
    }
  };

  const handleContractRemove = contract => {
    deleteContract(companyId, contract.id);
    setPreparedContracts(prev => prev.filter(c => c.id !== contract.id));
  };

  const handleContinue = () => {
    const contractIds = preparedContracts.map(contract => contract.id);

    dispatch(showModal(
      () => (
        <AiExtractContractRequirementsModal
          companyId={companyId}
          contractIds={contractIds}
          onComplete={templateId => {
            if (onExtractionComplete) {
              onExtractionComplete(templateId);

              return;
            }

            dispatch(hideModal());
            history.replace(`/companies/${companyId}/create-templates/requirement-templates/${templateId}`);
          }}
        />
      ),
      {
        size: '4xl',
        title: 'Extracting Requirements...',
        actionRequired: true
      }
    ));
  };

  return (
    <Box overflow="auto">
      <Text maxW="80%" mx="auto" pb="3rem" textStyle="bodyLg500">
        Upload one or more contracts, then PINS will automatically create a template for you based their insurance requirements!
      </Text>

      <Box>
        <FileDropzone
          maxFiles={10}
          mx="auto"
          onFilesAdded={handleFileDrop}
        />
        <HStack justifyContent="center" my={4}>
          <Button
            variant="secondary"
            onClick={() => dispatch(hideModal())}
          >
            Cancel
          </Button>
          <Button
            isDisabled={preparedContracts.length === 0}
            onClick={handleContinue}
          >
            {preparedContracts.length === 0 ? 'Upload Contracts to Continue' : 'Start Extraction'}
          </Button>
        </HStack>

        <FakeUploadProgressBar
          isComplete={!isUploading}
          isUploading={isUploading}
          label="Uploading contract(s)..."
          onDone={() => {}}
        />

        <Box maxH="150px" overflow="auto">

          {preparedContracts.map(contract => (
            <Flex
              alignItems="center"
              background="gradient.primary"
              borderRadius="4xl"
              color="white"
              flexFlow="row wrap"
              height="52px"
              justifyContent="space-between"
              key={`${contract.id}`}
              minW="405px"
              my={2}
              px={4}
              py={2}
              textStyle="bodyMd500"
            >
              <Text>{contract.file_name}</Text>
              <IconButton
                aria-label="close"
                icon={<Icon as={BsX} boxSize="18px" />}
                size="xs"
                variant="textWhite"
                onClick={() => handleContractRemove(contract)}
              />
            </Flex>
          ))}
        </Box>

      </Box>
    </Box>

  );
};

TemplatesFromContractModal.propTypes = {
  companyId: PropTypes.number.isRequired,
  /** Overrides the default navigation to the template builder once extraction finishes */
  onExtractionComplete: PropTypes.func
};

export default TemplatesFromContractModal;
